import type { ReactNode } from "react";
import type { ApiMeta } from "../../lib/api";
import { Button } from "./Button";
import { MicroLabel } from "./Text";

/**
 * The dense operational table.
 *
 * A real `<table>`, not a grid of divs: these are tabular data, and the semantic
 * element is what gives a screen reader row/column context for free. Zebra
 * striping is deliberately absent — with hairline row rules and tabular figures,
 * stripes add noise the alignment already handles.
 *
 * Rows are `stage`-animated with an index so a page reads top-to-bottom as it
 * arrives (one staged reveal, per the design direction), and the whole thing
 * scrolls inside its own container so a wide table never scrolls the page body.
 */

export function TableFrame({ children }: { readonly children: ReactNode }) {
  return (
    <div className="-mx-gutter overflow-x-auto px-gutter">
      <table className="w-full min-w-full border-collapse text-left">{children}</table>
    </div>
  );
}

export interface Column {
  readonly key: string;
  readonly label: string;
  /** Right-aligns the column. For figures, which are compared down the column. */
  readonly numeric?: boolean;
  readonly width?: string;
}

export function TableHead({ columns }: { readonly columns: readonly Column[] }) {
  return (
    <thead>
      <tr className="border-b border-rule-strong">
        {columns.map((column) => (
          <th
            key={column.key}
            scope="col"
            style={column.width === undefined ? undefined : { width: column.width }}
            // `pr-4` matches `Cell`. Without it a right-aligned header butts
            // against the next one and the two read as a single word.
            className={`pb-2 pr-4 pt-0 align-bottom ${column.numeric === true ? "text-right" : "text-left"}`}
          >
            <MicroLabel>{column.label}</MicroLabel>
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function TableRow({
  children,
  index,
  onActivate,
  isFaulted = false,
}: {
  readonly children: ReactNode;
  readonly index: number;
  /** Makes the row behave as a link target, keyboard included. */
  readonly onActivate?: () => void;
  /** Marks the row with a signal-coloured left edge. Reserved for real faults. */
  readonly isFaulted?: boolean;
}) {
  const interactive = onActivate !== undefined;

  return (
    <tr
      className={[
        "stage border-b border-rule",
        "transition-colors duration-instant ease-swiss",
        interactive ? "cursor-pointer hover:bg-paper-sunken focus-within:bg-paper-sunken" : "",
        // A 2px inset edge rather than a tinted row: the fault stays visible when
        // the row is also hovered, and it does not fight the status colour.
        isFaulted ? "shadow-[inset_2px_0_0_0_var(--color-signal)]" : "",
      ].join(" ")}
      style={{ ["--stage-index" as string]: index }}
      {...(interactive
        ? {
            onClick: onActivate,
            // Rows are reachable and activatable from the keyboard. A table of
            // clickable rows that only responds to a mouse is not finished.
            tabIndex: 0,
            onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onActivate();
              }
            },
          }
        : {})}
    >
      {children}
    </tr>
  );
}

export function Cell({
  children,
  numeric = false,
  muted = false,
  className = "",
}: {
  readonly children: ReactNode;
  readonly numeric?: boolean;
  readonly muted?: boolean;
  readonly className?: string;
}) {
  return (
    <td
      className={[
        "py-2 pr-4 align-middle text-data",
        numeric ? "text-right" : "text-left",
        muted ? "text-ink-faint" : "text-ink",
        className,
      ].join(" ")}
    >
      {children}
    </td>
  );
}

/**
 * Page controls, and the honest total.
 *
 * `meta.total` is the count *before* any client-side filter the server applied
 * after its query — the fleet endpoint filters on connection state in memory
 * (spec 10.3) and says so. So this prints the range it is actually showing
 * alongside the total rather than claiming the two agree.
 */
export function Pagination({
  meta,
  shown,
  onPage,
}: {
  readonly meta: ApiMeta | undefined;
  readonly shown: number;
  readonly onPage: (page: number) => void;
}) {
  if (meta === undefined) return null;

  const first = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const last = (meta.page - 1) * meta.limit + shown;
  const hasMore = last < meta.total;

  return (
    <div className="flex items-center justify-between gap-4 border-t border-rule pt-3">
      <MicroLabel>{meta.total === 0 ? "No rows" : `${first}–${last} of ${meta.total}`}</MicroLabel>
      <div className="flex items-center gap-2">
        <Button variant="ghost" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}>
          Previous
        </Button>
        <Button variant="ghost" disabled={!hasMore} onClick={() => onPage(meta.page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
